<template>
  <!-- 邮件账户自定义字段管理模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleCancel"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              自定义字段管理
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              为邮件账户添加自定义属性字段
            </p>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="-mb-px flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-1 sm:flex-initial',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 创建字段 Tab -->
        <div v-show="activeTab === 'create'" class="space-y-6">
          <div class="bg-gray-50 p-4 rounded-lg mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-3">创建新字段</label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  v-model="newField.name"
                  type="text"
                  placeholder="字段名称（英文）"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  :disabled="submitting"
                  @keyup.enter="handleCreateField"
                />
              </div>
              <div>
                <input
                  v-model="newField.label"
                  type="text"
                  placeholder="显示标签（中文）"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  :disabled="submitting"
                />
              </div>
            </div>
            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="relative" ref="fieldTypeSelectRef" @click.stop>
                <!-- 触发器 -->
                <div
                  @click.stop="toggleFieldTypeDropdown"
                  class="relative w-full h-10 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                  :class="{ 
                    'border-blue-500 ring-1 ring-blue-500': isFieldTypeOpen,
                    'opacity-50 cursor-not-allowed': submitting
                  }"
                >
                  <span class="flex-1 truncate text-sm text-gray-900">
                    {{ newField.field_type ? fieldTypeLabels[newField.field_type] : '选择字段类型' }}
                  </span>
                  <span class="pointer-events-none flex items-center pl-2">
                    <svg
                      class="h-5 w-5 text-gray-400 transition-transform duration-200"
                      :class="{ 'rotate-180': isFieldTypeOpen }"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                    </svg>
                  </span>
                </div>

                <!-- 下拉列表 -->
                <transition
                  enter-active-class="transition duration-100 ease-out"
                  enter-from-class="transform scale-95 opacity-0"
                  enter-to-class="transform scale-100 opacity-100"
                  leave-active-class="transition duration-75 ease-in"
                  leave-from-class="transform scale-100 opacity-100"
                  leave-to-class="transform scale-95 opacity-0"
                >
                  <div
                    v-show="isFieldTypeOpen && !submitting"
                    class="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-48 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                    style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;"
                  >
                    <!-- 选择字段类型选项 -->
                    <div
                      @click.stop="selectFieldType('')"
                      class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                      :class="{ 'bg-blue-100 text-blue-900': !newField.field_type }"
                    >
                      <span class="block truncate text-sm font-medium">
                        选择字段类型
                      </span>
                      <span
                        v-if="!newField.field_type"
                        class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                      >
                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                        </svg>
                      </span>
                    </div>

                    <!-- 字段类型选项 -->
                    <div
                      v-for="type in fieldTypeOptions"
                      :key="type.value"
                      @click.stop="selectFieldType(type.value)"
                      class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                      :class="{ 'bg-blue-100 text-blue-900': newField.field_type === type.value }"
                    >
                      <span class="block truncate text-sm font-medium">
                        {{ type.label }}
                      </span>
                      <span
                        v-if="newField.field_type === type.value"
                        class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                      >
                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </transition>
              </div>
              <div>
                <input
                  v-model="newField.placeholder"
                  type="text"
                  placeholder="占位符文本"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  :disabled="submitting"
                />
              </div>
            </div>
            
            <!-- 选项配置（仅在选择类型时显示） -->
            <div v-if="newField.field_type === 'choice' || newField.field_type === 'multiple_choice'" class="mt-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">选项配置</label>
              <div class="space-y-2">
                <div v-for="(choice, index) in newField.choices" :key="index" class="flex gap-2">
                  <input
                    v-model="choice.value"
                    type="text"
                    placeholder="选项值"
                    class="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <input
                    v-model="choice.label"
                    type="text"
                    placeholder="显示文本"
                    class="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <button
                    @click="removeChoice(index)"
                    class="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </button>
                </div>
                <button
                  @click="addChoice"
                  class="w-full px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  + 添加选项
                </button>
              </div>
            </div>

            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  v-model="newField.help_text"
                  type="text"
                  placeholder="帮助文本"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  :disabled="submitting"
                />
              </div>
              <div class="flex items-center space-x-4">
                <label class="flex items-center">
                  <input
                    v-model="newField.is_required"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span class="ml-2 text-sm text-gray-700">必填字段</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="newField.is_searchable"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span class="ml-2 text-sm text-gray-700">支持搜索</span>
                </label>
              </div>
            </div>
            
            <div class="mt-6 flex justify-end">
              <button
                @click="handleCreateField"
                :disabled="!canCreateField || submitting"
                class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {{ submitting ? '创建中...' : '创建字段' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 字段管理 Tab -->
        <div v-show="activeTab === 'manage'" class="space-y-6">
          <!-- 搜索框 -->
          <div class="mb-4">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索字段..."
              class="w-full h-10 px-4 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          <!-- 字段列表 -->
          <div v-if="loading" class="text-center py-8">
            <div class="text-gray-500">加载中...</div>
          </div>
          
          <div v-else-if="filteredFields.length === 0" class="text-center py-8">
            <div class="text-gray-500">
              {{ fields.length === 0 ? '暂无自定义字段' : '没有匹配的字段' }}
            </div>
          </div>
          
          <div v-else class="space-y-3">
            <draggable
              v-model="filteredFields"
              group="fields"
              @end="handleFieldSort"
              item-key="id"
              class="space-y-3"
            >
              <template #item="{ element: field }">
                <div class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3 flex-1">
                      <!-- 拖拽手柄 -->
                      <div class="cursor-move text-gray-400 hover:text-gray-600">
                        <Bars3Icon class="h-5 w-5" />
                      </div>
                      
                      <div class="flex-1">
                        <div class="flex items-center space-x-1">
                          <span class="font-medium text-gray-900">{{ field.label }}</span>
                          <span class="text-sm text-gray-500">({{ field.name }})</span>
                        </div>
                        <div class="flex items-center space-x-4 mt-1">
                          <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                            {{ getFieldTypeLabel(field.field_type) }}
                          </span>
                          <span v-if="field.is_required" class="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                            必填
                          </span>
                          <span v-if="field.is_searchable" class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                            搜索
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="flex items-center space-x-2">
                      <button
                        @click="handleEditField(field)"
                        class="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title="编辑字段"
                      >
                        <PencilIcon class="h-4 w-4" />
                      </button>
                      <button
                        @click="handleDeleteField(field)"
                        class="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        title="删除字段"
                      >
                        <TrashIcon class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>

        </div>

        <!-- 底部按钮 -->
        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="handleCancel"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 编辑字段模态框 -->
  <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-[100]">
    <div class="bg-white rounded-lg shadow-xl w-[90vw] max-w-[90vw] sm:max-w-lg mx-auto">
      <div class="p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">编辑字段</h3>
          <button @click="cancelEdit" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">字段名称</label>
            <input
              v-model="editingField.name"
              type="text"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              :disabled="updating"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">显示标签</label>
            <input
              v-model="editingField.label"
              type="text"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              :disabled="updating"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">占位符</label>
            <input
              v-model="editingField.placeholder"
              type="text"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              :disabled="updating"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">帮助文本</label>
            <input
              v-model="editingField.help_text"
              type="text"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              :disabled="updating"
            />
          </div>
          
          <!-- 选择类型字段的选项配置 -->
          <div v-if="editingField.field_type === 'choice' || editingField.field_type === 'multiple_choice'">
            <label class="block text-sm font-medium text-gray-700 mb-2">选项配置</label>
            <div class="space-y-2">
              <div v-for="(choice, index) in editingField.choices" :key="index" class="flex gap-2">
                <input
                  v-model="choice.value"
                  type="text"
                  placeholder="选项值"
                  class="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <input
                  v-model="choice.label"
                  type="text"
                  placeholder="显示文本"
                  class="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <button
                  @click="removeEditChoice(index)"
                  class="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon class="h-4 w-4" />
                </button>
              </div>
              <button
                @click="addEditChoice"
                class="w-full px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
              >
                + 添加选项
              </button>
            </div>
          </div>
          
          <div class="flex items-center space-x-4">
            <label class="flex items-center">
              <input
                v-model="editingField.is_required"
                type="checkbox"
                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="ml-2 text-sm text-gray-700">必填字段</span>
            </label>
            <label class="flex items-center">
              <input
                v-model="editingField.is_searchable"
                type="checkbox"
                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span class="ml-2 text-sm text-gray-700">支持搜索</span>
            </label>
          </div>
        </div>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="cancelEdit"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            取消
          </button>
          <button
            @click="handleUpdateField"
            class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useToast } from 'vue-toastification'
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  CogIcon,
  Bars3Icon
} from '@heroicons/vue/24/outline'
import draggable from 'vuedraggable'
import emailAccountService from '@/services/emailAccountService'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

const toast = useToast()

// 响应式数据
const activeTab = ref('create')
const loading = ref(false)
const submitting = ref(false)
const updating = ref(false)
const searchQuery = ref('')
const showEditModal = ref(false)

// Tabs 配置
const tabs = [
  { id: 'create', name: '创建字段' },
  { id: 'manage', name: '管理字段' }
]

// 新字段表单
const newField = ref({
  name: '',
  label: '',
  field_type: '',
  placeholder: '',
  is_required: false,
  is_list_display: false,
  choices: []
})

// 下拉框状态
const isFieldTypeOpen = ref(false)
const fieldTypeSelectRef = ref(null)

// 字段类型映射
const fieldTypeLabels = {
  text: '文本',
  textarea: '多行文本',
  number: '数字',
  decimal: '小数',
  date: '日期',
  boolean: '是/否',
  choice: '单选',
  multiple_choice: '多选',
  url: '网址',
  email: '邮箱'
}

// 字段类型选项数组
const fieldTypeOptions = [
  { value: 'text', label: '文本' },
  { value: 'textarea', label: '多行文本' },
  { value: 'number', label: '数字' },
  { value: 'decimal', label: '小数' },
  { value: 'date', label: '日期' },
  { value: 'boolean', label: '是/否' },
  { value: 'choice', label: '单选' },
  { value: 'multiple_choice', label: '多选' },
  { value: 'url', label: '网址' },
  { value: 'email', label: '邮箱' }
]

// 编辑字段表单
const editingField = ref({})

// 字段列表
const fields = ref([])

// 计算属性
const canCreateField = computed(() => {
  return newField.value.name && 
         newField.value.label && 
         newField.value.field_type
})

const canUpdateField = computed(() => {
  return editingField.value.name && 
         editingField.value.label
})

const filteredFields = computed(() => {
  if (!searchQuery.value) {
    return fields.value
  }
  const query = searchQuery.value.toLowerCase()
  return fields.value.filter(field => 
    field.name.toLowerCase().includes(query) ||
    field.label.toLowerCase().includes(query)
  )
})

// 获取字段类型显示名称
const getFieldTypeLabel = (fieldType) => {
  const typeMap = {
    'text': '文本',
    'textarea': '多行文本',
    'number': '数字',
    'decimal': '小数',
    'date': '日期',
    'boolean': '是/否',
    'choice': '单选',
    'multiple_choice': '多选',
    'url': '网址',
    'email': '邮箱'
  }
  return typeMap[fieldType] || fieldType
}

// 重置新字段表单
const resetNewField = () => {
  newField.value = {
    name: '',
    label: '',
    field_type: '',
    placeholder: '',
    help_text: '',
    is_required: false,
    is_searchable: false,
    choices: []
  }
}

// 选项管理
const addChoice = () => {
  newField.value.choices.push({ value: '', label: '' })
}

const removeChoice = (index) => {
  newField.value.choices.splice(index, 1)
}

// 下拉框控制方法
const toggleFieldTypeDropdown = () => {
  if (!submitting.value) {
    isFieldTypeOpen.value = !isFieldTypeOpen.value
  }
}

const selectFieldType = (value) => {
  newField.value.field_type = value
  isFieldTypeOpen.value = false
}

const closeAllDropdowns = () => {
  isFieldTypeOpen.value = false
}

const handleClickOutside = (event) => {
  if (fieldTypeSelectRef.value && !fieldTypeSelectRef.value.contains(event.target)) {
    closeAllDropdowns()
  }
}

const addEditChoice = () => {
  if (!editingField.value.choices) {
    editingField.value.choices = []
  }
  editingField.value.choices.push({ value: '', label: '' })
}

const removeEditChoice = (index) => {
  editingField.value.choices.splice(index, 1)
}

// 字段排序处理
const handleFieldSort = (event) => {
  // 拖拽完成后，可以在这里保存新的排序
  console.log('字段排序已更新', event)
}

// 加载字段列表
const loadFields = async () => {
  loading.value = true
  try {
    const response = await emailAccountService.getCustomFields({
      page_size: 100,
      ordering: 'sort_order'
    })
    if (response.success && response.data.results) {
      fields.value = response.data.results
    }
  } catch (error) {
    console.error('加载自定义字段失败:', error)
    toast.error('加载字段列表失败')
  } finally {
    loading.value = false
  }
}

// 创建字段
const handleCreateField = async () => {
  if (!canCreateField.value) return

  submitting.value = true
  try {
    // 准备提交的数据
    const fieldData = { ...newField.value }
    
    // 如果是选择类型字段但没有选项，添加默认选项
    if ((fieldData.field_type === 'choice' || fieldData.field_type === 'multiple_choice') && fieldData.choices.length === 0) {
      fieldData.choices = [
        { value: 'option1', label: '选项1' },
        { value: 'option2', label: '选项2' }
      ]
    }

    const response = await emailAccountService.createCustomField(fieldData)
    if (response.success) {
      toast.success('字段创建成功')
      resetNewField()
      await loadFields()
      emit('success')
    }
  } catch (error) {
    console.error('创建字段失败:', error)
    if (error.response?.data?.name) {
      toast.error('字段名称已存在')
    } else {
      toast.error('创建字段失败，请重试')
    }
  } finally {
    submitting.value = false
  }
}

// 开始编辑字段
const handleEditField = (field) => {
  editingField.value = {
    ...field,
    choices: field.choices ? [...field.choices] : []
  }
  showEditModal.value = true
}

// 取消编辑
const cancelEdit = () => {
  showEditModal.value = false
  editingField.value = {}
}

// 更新字段
const handleUpdateField = async () => {
  if (!canUpdateField.value) return

  updating.value = true
  try {
    const response = await emailAccountService.updateCustomField(editingField.value.id, editingField.value)
    if (response.success) {
      toast.success('字段更新成功')
      showEditModal.value = false
      await loadFields()
      emit('success')
    }
  } catch (error) {
    console.error('更新字段失败:', error)
    toast.error('更新字段失败，请重试')
  } finally {
    updating.value = false
  }
}

// 删除字段
const handleDeleteField = async (field) => {
  if (!confirm(`确定要删除字段"${field.label}"吗？此操作不可撤销。`)) {
    return
  }

  field.deleting = true
  try {
    const response = await emailAccountService.deleteCustomField(field.id)
    if (response.success) {
      toast.success('字段删除成功')
      await loadFields()
      emit('success')
    }
  } catch (error) {
    console.error('删除字段失败:', error)
    toast.error('删除字段失败，请重试')
  } finally {
    field.deleting = false
  }
}

// 取消/关闭
const handleCancel = () => {
  emit('close')
}

// 监听字段类型变化
watch(() => newField.value.field_type, (newType) => {
  if (newType === 'choice' || newType === 'multiple_choice') {
    if (newField.value.choices.length === 0) {
      newField.value.choices = [
        { value: '', label: '' }
      ]
    }
  } else {
    newField.value.choices = []
  }
})

// 监听模态框显示状态
watch(() => props.show, (show) => {
  if (show) {
    loadFields()
    resetNewField()
    activeTab.value = 'create'
    searchQuery.value = ''
  }
})

// 组件挂载时加载数据
onMounted(() => {
  if (props.show) {
    loadFields()
  }
  // 添加点击外部关闭下拉框的事件监听
  document.addEventListener('click', handleClickOutside)
})

// 清理事件监听器
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
